/**
 * <cba-product-card> — a product card (snippets/cba-product-card). Loaded
 * once from layout/theme.liquid.
 *
 *   <cba-product-card class="cba-product-card" data-product-id data-product-handle data-product-url>
 *     [data-card-gallery] .cba-product-card__slide … [data-card-dot]
 *     [data-card-prev] [data-card-next] [data-card-wish] .cba-swatch …
 *   </cba-product-card>
 *
 * - The gallery: the arrows scroll one image; the dots follow the image
 *   shown; the arrow that would do nothing at either end is disabled
 *   (hidden by CSS).
 * - A colour swatch shows that colour's photo, price, compare-at price and
 *   discount badge, and sets the variant the Add form sends.
 * - The wishlist heart: a list in localStorage ("cba-wishlist"); a change
 *   says so with wishlist:change on document.
 * - A card starts itself wherever it lands: on the page, in search results,
 *   in the cart's "You may also like", in the theme editor. It was one
 *   script with document-level handlers that code injecting cards had to
 *   call again through a global (DESIGN-PLAN 7.G6, shell audit S18.2).
 * - Add opens the quick-add picker (<cba-quick-add-host>), which takes the
 *   card's Add form.
 */
if (!customElements.get('cba-product-card')) {
  const STORAGE_KEY = 'cba-wishlist';

  const readList = () => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch (e) { return []; }
  };
  const writeList = (list) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) { /* storage blocked: the heart still toggles for this view */ }
  };
  const inList = (id) => readList().indexOf(String(id)) !== -1;
  const toggleList = (id) => {
    const list = readList();
    const productId = String(id);
    const at = list.indexOf(productId);
    const inWishlist = at === -1;
    if (inWishlist) list.push(productId);
    else list.splice(at, 1);
    writeList(list);
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { productId, inWishlist } }));
    return inWishlist;
  };

  class CbaProductCard extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.addEventListener('click', (e) => this.onClick(e), { signal });

      const productId = this.getAttribute('data-product-id');
      const heart = this.querySelector('[data-card-wish]');
      if (heart && productId && inList(productId)) heart.setAttribute('aria-pressed', 'true');

      const gallery = this.gallery;
      if (gallery) {
        gallery.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.updateDots(), 60);
        }, { signal });
        this.updateDots();
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      clearTimeout(this.scrollTimer);
    }

    get gallery() { return this.querySelector('[data-card-gallery]'); }

    currentIndex() {
      const gallery = this.gallery;
      return gallery && gallery.offsetWidth ? Math.round(gallery.scrollLeft / gallery.offsetWidth) : 0;
    }

    updateDots() {
      const gallery = this.gallery;
      if (!gallery) return;
      const at = this.currentIndex();
      this.querySelectorAll('[data-card-dot]').forEach((dot, i) => dot.classList.toggle('cba-carousel-dot--active', i === at));
      const last = gallery.querySelectorAll('.cba-product-card__slide').length - 1;
      const prev = this.querySelector('[data-card-prev]');
      const next = this.querySelector('[data-card-next]');
      if (prev) prev.disabled = at <= 0;
      if (next) next.disabled = at >= last;
    }

    goTo(index) {
      const gallery = this.gallery;
      if (!gallery) return;
      const count = gallery.querySelectorAll('.cba-product-card__slide').length;
      const to = Math.max(0, Math.min(index, count - 1));
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      gallery.scrollTo({ left: to * gallery.offsetWidth, behavior: still ? 'auto' : 'smooth' });
    }

    onClick(e) {
      const heart = e.target.closest('[data-card-wish]');
      if (heart) {
        const productId = this.getAttribute('data-product-id');
        if (!productId) return;
        e.preventDefault();
        heart.setAttribute('aria-pressed', toggleList(productId) ? 'true' : 'false');
        return;
      }

      if (e.target.closest('[data-card-prev]')) { e.preventDefault(); this.goTo(this.currentIndex() - 1); return; }
      if (e.target.closest('[data-card-next]')) { e.preventDefault(); this.goTo(this.currentIndex() + 1); return; }

      /* The card's own swatches only. The same .cba-swatch is used, as a
         radio with a label, by the in-card quick-add panel, which sits
         inside this card: taking its click would stop the radio (the
         variant never changed) and light two swatches (fixed 2026-05-25). */
      const swatch = e.target.closest('.cba-swatch');
      if (swatch && !swatch.closest('.cba-quick-add')) { e.preventDefault(); this.showColour(swatch); }
    }

    showColour(swatch) {
      this.querySelectorAll('.cba-swatch').forEach((s) => s.setAttribute('aria-pressed', 'false'));
      swatch.setAttribute('aria-pressed', 'true');

      const variantId = swatch.getAttribute('data-variant-id');
      const image = swatch.getAttribute('data-image');
      const price = swatch.getAttribute('data-price');
      const compare = swatch.getAttribute('data-compare');
      const onSale = swatch.getAttribute('data-on-sale') === 'true';
      const percent = swatch.getAttribute('data-discount-pct');

      const firstImage = this.querySelector('[data-cba-primary-img]');
      if (firstImage && image) {
        firstImage.removeAttribute('srcset');
        firstImage.setAttribute('src', image);
      }
      const gallery = this.gallery;
      if (gallery) gallery.scrollTo({ left: 0, behavior: 'auto' });

      const idInput = this.querySelector('[data-cba-variant-id]');
      if (idInput && variantId) idInput.value = variantId;

      const priceEl = this.querySelector('[data-cba-price]');
      const compareEl = this.querySelector('[data-cba-compare]');
      const badge = this.querySelector('[data-cba-discount-badge]');
      if (priceEl && price) priceEl.textContent = price;
      if (compareEl) {
        compareEl.hidden = !(onSale && compare);
        if (onSale && compare) compareEl.textContent = compare;
      }
      if (badge) {
        badge.hidden = !(onSale && percent);
        if (onSale && percent) badge.textContent = '-' + percent + '%';
      }
    }
  }

  customElements.define('cba-product-card', CbaProductCard);
}
